import { useState } from "react";
import { LifeBuoy, Plus, Send, ArrowLeft, MessageSquare } from "lucide-react";

import { api } from "../lib/api";
import { useResource } from "../lib/useApi";
import { date } from "../lib/fmt";
import { Sheet, Glass, Skeleton, Err, Empty, Pill } from "../ui";

const CATEGORIES = [
  ["design", "Design"], ["site", "Site work"], ["billing", "Billing"],
  ["delay", "Timeline"], ["material", "Materials"], ["other", "Something else"],
];

export function SupportSheet({ ctx, onClose }) {
  const { pid } = ctx;
  const { data, loading, error, reload } = useResource("/portal/tickets");
  const [view, setView] = useState({ kind: "list" });

  if (view.kind === "new") {
    return <NewTicket pid={pid} onBack={() => setView({ kind: "list" })} onDone={() => { setView({ kind: "list" }); reload(); }} onClose={onClose} />;
  }

  if (view.kind === "one") {
    const t = (data || []).find((x) => x.id === view.id);
    if (t) return <OneTicket ticket={t} onBack={() => setView({ kind: "list" })} onReplied={reload} onClose={onClose} />;
  }

  return (
    <Sheet title="Help" onClose={onClose}>
      <Err onDismiss={reload}>{error}</Err>

      <button className="btn primary block" style={{ marginBottom: 16 }} onClick={() => setView({ kind: "new" })}>
        <Plus size={16} /> Raise a request
      </button>

      {loading && <Skeleton h={70} />}

      {!loading && !data?.length && (
        <Empty
          icon={LifeBuoy}
          title="Nothing open"
          note="Anything you raise here reaches your project team directly, and stays on the record."
        />
      )}

      {(data || []).map((t) => (
        <Glass as="button" className="row" key={t.id} onClick={() => setView({ kind: "one", id: t.id })}>
          <span className="row-ic"><MessageSquare size={16} /></span>
          <span className="row-txt">
            <b>{t.subject}</b>
            <small className="mono">{t.ticket_no} · {date(t.created_at)}</small>
          </span>
          <span className="row-end">
            <Pill tone={t.status === "resolved" || t.status === "closed" ? "ok" : "warn"}>{t.status}</Pill>
            {t.replies?.length > 0 && <small>{t.replies.length} repl{t.replies.length === 1 ? "y" : "ies"}</small>}
          </span>
        </Glass>
      ))}
    </Sheet>
  );
}

function NewTicket({ pid, onBack, onDone, onClose }) {
  const [subject, setSubject] = useState("");
  const [body, setBody] = useState("");
  const [category, setCategory] = useState("other");
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");

  async function submit() {
    if (!subject.trim()) return setErr("Give your request a subject.");
    if (!body.trim()) return setErr("Describe what you need.");
    setBusy(true);
    setErr("");
    try {
      await api.post("/portal/tickets", { subject: subject.trim(), body: body.trim(), category, project_id: pid });
      onDone();
    } catch (e) {
      setErr(e.message);
      setBusy(false);
    }
  }

  return (
    <Sheet title={<BackHead label="Raise a request" onBack={onBack} />} onClose={onClose}>
      <Err onDismiss={() => setErr("")}>{err}</Err>

      <div className="field">
        <label htmlFor="tk-sub">Subject</label>
        <input id="tk-sub" maxLength={200} value={subject} placeholder="Wardrobe shutter alignment" onChange={(e) => setSubject(e.target.value)} />
      </div>

      <div className="field">
        <label htmlFor="tk-cat">What is it about</label>
        <select id="tk-cat" value={category} onChange={(e) => setCategory(e.target.value)}>
          {CATEGORIES.map(([v, l]) => <option key={v} value={v}>{l}</option>)}
        </select>
      </div>

      <div className="field">
        <label htmlFor="tk-body">Tell us what you need</label>
        <textarea id="tk-body" rows={5} maxLength={5000} value={body} onChange={(e) => setBody(e.target.value)} />
      </div>

      <button className="btn primary block" onClick={submit} disabled={busy}>
        {busy ? "Sending…" : "Send to my team"}
      </button>
    </Sheet>
  );
}

function OneTicket({ ticket, onBack, onReplied, onClose }) {
  const [text, setText] = useState("");
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");
  const closed = ticket.status === "closed";

  async function send() {
    if (!text.trim()) return;
    setBusy(true);
    setErr("");
    try {
      await api.post("/portal/ticket-reply", { ticket_id: ticket.id, body: text.trim() });
      setText("");
      onReplied();
    } catch (e) {
      setErr(e.message);
    } finally {
      setBusy(false);
    }
  }

  return (
    <Sheet
      title={<BackHead label={ticket.subject} onBack={onBack} />}
      onClose={onClose}
      footer={
        !closed && (
          <div className="vh-ask">
            <textarea
              value={text}
              placeholder="Reply to your team…"
              onChange={(e) => setText(e.target.value)}
              aria-label="Your reply"
            />
            <button className="vh-send" onClick={send} disabled={busy || !text.trim()} aria-label="Send reply">
              <Send size={17} />
            </button>
          </div>
        )
      }
    >
      <Err onDismiss={() => setErr("")}>{err}</Err>

      <div style={{ display: "flex", gap: 8, marginBottom: 14 }}>
        <Pill tone={closed || ticket.status === "resolved" ? "ok" : "warn"}>{ticket.status}</Pill>
        <span className="mono muted" style={{ fontSize: ".74rem", alignSelf: "center" }}>{ticket.ticket_no}</span>
      </div>

      <div className="vh-log">
        <div className="vh-msg me">{ticket.body}</div>
        {(ticket.replies || []).map((r) => (
          <div key={r.id} className={"vh-msg " + (Number(r.from_client) ? "me" : "her")}>
            {!Number(r.from_client) && (
              <b style={{ display: "block", fontSize: ".72rem", color: "var(--mauve-deep)", marginBottom: 3 }}>
                {r.author_name}
              </b>
            )}
            {r.body}
          </div>
        ))}
      </div>

      {closed && (
        <p className="muted" style={{ marginTop: 16, fontSize: ".84rem" }}>
          This request is closed. Raise a new one if anything else comes up.
        </p>
      )}
    </Sheet>
  );
}

function BackHead({ label, onBack }) {
  return (
    <div style={{ display: "flex", alignItems: "center", gap: 10, flex: 1, minWidth: 0 }}>
      <button className="bar-btn" onClick={onBack} aria-label="Back">
        <ArrowLeft size={18} />
      </button>
      <h2 style={{ margin: 0, fontFamily: "var(--font-head)", fontSize: "1.35rem", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
        {label}
      </h2>
    </div>
  );
}
